<template>
  <div class="space-y-4">
    <h3 class="text-lg font-medium text-center mb-4">{{ $t('completeProfile') }}</h3>
    
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div v-for="field in customFields" :key="field.meta_key" class="space-y-2">
        <label :for="field.meta_key" class="block text-sm font-medium text-gray-700">
          {{ field.name }}
          <span v-if="field.status === 'required'" class="text-red-500">*</span>
        </label>
        
        <!-- Text input -->
        <input
          v-if="field.type === 'text' || !field.type"
          :id="field.meta_key"
          v-model="formData[field.meta_key]"
          type="text"
          :required="field.status === 'required'"
          :maxlength="field.max_length"
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
        
        <!-- Number input -->
        <input
          v-else-if="field.type === 'number'"
          :id="field.meta_key"
          v-model="formData[field.meta_key]"
          type="number"
          :required="field.status === 'required'"
          :max="field.max_length"
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
        
        <!-- Textarea -->
        <textarea
          v-else-if="field.type === 'textarea'"
          :id="field.meta_key"
          v-model="formData[field.meta_key]"
          :required="field.status === 'required'"
          :maxlength="field.max_length"
          rows="4"
          class="appearance-none rounded-[20px] relative block w-full px-3 py-2 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        ></textarea>
        
        <!-- Select dropdown -->
        <select
          v-else-if="field.type === 'select'"
          :id="field.meta_key"
          v-model="formData[field.meta_key]"
          :required="field.status === 'required'"
          class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        >
          <option value="">{{ $t('pleaseSelect') }}</option>
          <option v-for="option in getFieldOptions(field)" :key="option" :value="option">{{ option }}</option>
        </select>
        
        <!-- Checkbox -->
        <div v-else-if="field.type === 'checkbox'" class="flex items-start">
          <div class="flex items-center h-5">
            <input
              :id="field.meta_key"
              v-model="formData[field.meta_key]"
              type="checkbox"
              :required="field.status === 'required'"
              class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
          </div>
          <div class="ml-3 text-sm">
            <label :for="field.meta_key" class="font-medium text-gray-700">
              {{ field.checkbox_label || field.name }}
            </label>
          </div>
        </div>
        
        <!-- Radio buttons -->
        <div v-else-if="field.type === 'radio'" class="space-y-2">
          <div v-for="option in getFieldOptions(field)" :key="option" class="flex items-center">
            <input
              :id="`${field.meta_key}_${option}`"
              v-model="formData[field.meta_key]"
              :name="field.meta_key"
              type="radio"
              :value="option"
              :required="field.status === 'required'"
              class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
            />
            <label :for="`${field.meta_key}_${option}`" class="ml-3 block text-sm font-medium text-gray-700">
              {{ option }}
            </label>
          </div>
        </div>
        
        <!-- File upload (uploader) -->
        <div v-else-if="field.type === 'uploader'" class="space-y-2">
          <input
            :id="field.meta_key"
            type="file"
            :ref="el => setFileInputRef(field.meta_key, el)"
            class="hidden"
            :accept="getUploaderAccept(field)"
            @change="(e) => onFileChange(e, field)"
          />
          <div class="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              @click="triggerFileInput(field.meta_key)"
              :disabled="uploading[field.meta_key]"
              class="px-4 py-2 rounded-[32px] border border-[#e6e6e6] bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              <span v-if="uploading[field.meta_key]">{{ $t('uploadingFile') }}</span>
              <span v-else>{{ $t('chooseFile') }}</span>
            </button>
            <span v-if="formData[field.meta_key] && !uploading[field.meta_key]" class="text-sm text-gray-600">
              {{ uploadedFileName[field.meta_key] || $t('uploadSuccess') }}
            </span>
            <span v-else-if="!formData[field.meta_key] && !uploading[field.meta_key]" class="text-sm text-gray-400">
              {{ $t('noFileChosen') }}
            </span>
          </div>
        </div>
        
        <!-- Character count for text and textarea -->
        <div v-if="(field.type === 'text' || field.type === 'textarea') && field.max_length" class="text-xs text-gray-500 text-right">
          {{ formData[field.meta_key]?.length || 0 }}/{{ field.max_length }}
        </div>
      </div>

      <div class="flex gap-4">
        <button
          type="submit"
          class="continue-button flex-1 flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
          :disabled="loading"
        >
          <span v-if="loading" class="spinner-loading"></span>
          <span v-else>{{ $t('continue') }}</span>
        </button>
        
        <button
          v-if="hasOptionalFieldsOnly"
          type="button"
          @click="skipCustomFields"
          class="flex-1 flex justify-center items-center py-2 px-4 border text-sm font-medium rounded-[32px] h-14 bg-white border-main text-main hover:bg-gray-50 focus:outline-none focus:ring-0"
        >
          {{ $t('skip') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { showError } from '../../utils/swal'

const props = defineProps({
  customFields: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['submit', 'skip'])
const { t } = useI18n()
const loading = ref(false)
const formData = ref({})
const fileInputRefs = ref({})
const uploading = ref({})
const uploadedFileName = ref({})

const smsir = typeof window !== 'undefined' ? (window.sms_ir_login || {}) : {}

const hasOptionalFieldsOnly = computed(() => {
  return !props.customFields.some(field => field.status === 'required')
})

const setFileInputRef = (metaKey, el) => {
  if (el) fileInputRefs.value[metaKey] = el
}

const triggerFileInput = (metaKey) => {
  const el = fileInputRefs.value[metaKey]
  if (el) el.click()
}

const onFileChange = async (e, field) => {
  const file = e.target.files?.[0]
  if (!file) return
  const metaKey = field.meta_key
  uploading.value[metaKey] = true
  uploadedFileName.value[metaKey] = ''
  try {
    const formDataUpload = new FormData()
    formDataUpload.append('action', 'smsir_upload_custom_field_file')
    formDataUpload.append('nonce', smsir.nonce || '')
    formDataUpload.append('file', file)
    formDataUpload.append('meta_key', metaKey)
    const response = await fetch(smsir.ajaxUrl || '', {
      method: 'POST',
      body: formDataUpload
    })
    const data = await response.json()
    if (data.success && data.data?.attachment_id) {
      formData.value[metaKey] = String(data.data.attachment_id)
      uploadedFileName.value[metaKey] = file.name
    } else {
      showError(data.data?.message || t('uploadError'))
      formData.value[metaKey] = ''
    }
  } catch (err) {
    showError(t('uploadError'))
    formData.value[metaKey] = ''
  } finally {
    uploading.value[metaKey] = false
    e.target.value = ''
  }
}

const getFieldOptions = (field) => {
  if (!field.options) return []
  return field.options.split(',').map(option => option.trim()).filter(option => option)
}

const getUploaderAccept = (field) => {
  const raw = field.accepted_file_types?.trim()
  if (!raw) return 'image/*,.pdf,.doc,.docx'
  return raw.split(',').map(ext => {
    const s = ext.trim()
    return s.startsWith('.') || s.includes('*') || s.includes('/') ? s : '.' + s
  }).join(',')
}

const handleSubmit = () => {
  const requiredFields = props.customFields.filter(field => field.status === 'required')
  const missingRequired = requiredFields.some(field => !formData.value[field.meta_key])
  
  if (missingRequired) {
    showError(t('customFieldsRequired'))
    return
  }
  
  loading.value = true
  emit('submit', formData.value)
}

const skipCustomFields = () => {
  emit('skip')
}

watch(() => loading.value, (newVal) => {
  if (!newVal) {
    loading.value = false
  }
})
</script> 